import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete
} from '@mui/material'
import { SALES_COMMISSION_INCENTIVE_COMPANIES } from '../../../constant/htg-companies'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  usePostSalesIncentive,
  usePatchSalesIncentive
} from '../../../api/sales-commission-incentive'
import { getErrorMessage } from '../../../api/utils'
import { useSnackbar } from 'notistack'

const schema = yup.object().shape({
  htgCompany: yup
    .string()
    .required('HTG company is required')
    .oneOf(Object.values(SALES_COMMISSION_INCENTIVE_COMPANIES), 'Invalid HTG company')
    .trim(),
  description: yup.string().required('Description is required').trim(),
  salesId: yup.string().required('Sales ID is required').trim(),
  amount: yup.number().typeError('Amount must be a number').required('Amount is required'),
  transactionYearMonth: yup
    .string()
    .required('Month is required')
    .matches(/^\d{4}$/, 'Must be in YYMM format')
    .test('is-valid-month', 'Invalid month (01-12)', (value) => {
      if (!value) return true
      const month = parseInt(value.substring(2), 10)
      return month >= 1 && month <= 12
    })
})

const defaultValues = {
  htgCompany: '',
  description: '',
  salesId: '',
  amount: '',
  transactionYearMonth: ''
}

const IncentiveDialog = ({ open, setOpen, data, refetch, allSalesIds = [], isLoadingSalesIds }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: createIncentive } = usePostSalesIncentive()
  const { mutateAsync: updateIncentive } = usePatchSalesIncentive(data?._id)

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({ resolver: yupResolver(schema) })

  const onClose = () => {
    setOpen(false)
    reset()
  }

  const onSubmit = async (values) => {
    try {
      const isEdit = Boolean(data)

      await (isEdit ? updateIncentive(values) : createIncentive(values))

      enqueueSnackbar(`Incentive ${isEdit ? 'updated' : 'created'}`, { variant: 'success' })

      refetch()
      onClose()
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error', style: { whiteSpace: 'pre-line' } })
    }
  }

  useEffect(
    () => reset(data ? { ...defaultValues, ...data } : { ...defaultValues }),
    [data, reset, open]
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{data ? 'Edit Incentive' : 'Add Incentive'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name='htgCompany'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    options={Object.values(SALES_COMMISSION_INCENTIVE_COMPANIES)}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='HTG Company'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='description'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Description'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='salesId'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    options={allSalesIds}
                    value={field.value}
                    loading={isLoadingSalesIds}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Sales ID'
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name='amount'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Amount'
                    type='number'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name='transactionYearMonth'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Month (YYMM)'
                    placeholder='e.g. 2605'
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isSubmitting}>
            {data ? 'UPDATE' : 'CREATE'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default IncentiveDialog
